import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2 } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { UserTerminal } from '@/shared/types/userTerminalTypes';
import { useTransferTerminal } from '@/domains/terminals/services/account-user-terminals';

interface TransferTerminalToAccountDialogProps {
  open: boolean;
  terminal: UserTerminal;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const TransferTerminalToAccountDialog = ({
  open,
  onOpenChange,
  onSuccess,
  terminal,
}: TransferTerminalToAccountDialogProps) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const transferTerminal = useTransferTerminal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganizationId) {
      return;
    }

    try {
      await transferTerminal.mutateAsync({
        terminalId: terminal.userTerminalId,
        destinationOrganizationId: selectedOrganizationId,
      });
      showSuccess(SUCCESS.TERMINAL.UPDATED_TYPE);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.UPDATE_TYPE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Transfer Terminal</DialogTitle>
          <DialogDescription>
            Select an account to transfer this terminal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {terminal.eligibleTargetOrganizations?.length > 0 &&
            <div className="space-y-2">
              <Label htmlFor="destination-account">Destination Account</Label>
              <Select
                value={selectedOrganizationId ?? undefined}
                onValueChange={(value) =>
                  setSelectedOrganizationId(value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Account" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(terminal?.eligibleTargetOrganizations)?.map((organization) => (
                    <SelectItem key={organization.organizationId} value={organization.organizationId}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          }

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={transferTerminal.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={transferTerminal.isPending || !selectedOrganizationId}>
              {transferTerminal.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
